import { useEffect, useMemo } from "react";
import { SegmentData } from "../../../types/assessment";
import useAssessmentAISummary from "./useAssessmentAISummary";

const useSummaryView = (selectedAssessment: any) => {
  const {
    data,
    errors,
    isLoading,
    chatInput,
    isEditMode,
    toggledViews,
    chatMessages,
    selectedViews,
    playingSegment,
    selectedSegments,
    expandedTranscripts,
    // Functions
    formatTitle,
    setChatInput,
    setToggledViews,
    setChatMessages,
    setSelectedViews,
    setPlayingSegment,
    splitTextIntoPoints,
    setSelectedSegments,
    setExpandedTranscripts,
    fetchAIAssessmentSummary,
  } = useAssessmentAISummary();

  const segmentData: SegmentData[] = useMemo(() => {
    if (data.length > 0) {
      return data.map((item: any, index: number) => ({
        id: `${item.id}-${index}`,
        title: formatTitle(item.segment_name),
        summary: item.segment_summary,
        positiveAspects: item.segment_positive,
        negativeAspects: item.segment_negative,
        transcript: "Transcript for this segment is not available.",
      }));
    }
    return [];
  }, [data]);

  useEffect(() => {
    if (segmentData.length > 0) {
      setToggledViews(new Set(segmentData.map((s) => s.id)));
      setSelectedSegments(new Set(segmentData.map((segment) => segment.id)));
    }
  }, [segmentData]);

  useEffect(() => {
    if (selectedAssessment?.value) {
      const assessment_type = selectedAssessment?.value;
      fetchAIAssessmentSummary(assessment_type);
    }
  }, [selectedAssessment?.value]);

  const togglePlay = (segmentId: string) => {
    if (playingSegment === segmentId) {
      setPlayingSegment(null);
    } else {
      setPlayingSegment(segmentId);
      setTimeout(() => setPlayingSegment(null), 3000);
    }
  };

  //  Not in use as of now

  const toggleTranscript = (segmentId: string) => {
    const newExpanded = new Set(expandedTranscripts);
    if (newExpanded.has(segmentId)) {
      newExpanded.delete(segmentId);
    } else {
      newExpanded.add(segmentId);
    }
    setExpandedTranscripts(newExpanded);
  };

  // Not is used as of now
  const handleChatSubmit = () => {
    if (!chatInput.trim()) return;
    const newMessages = [
      ...chatMessages,
      { role: "user" as const, content: chatInput },
      {
        role: "assistant" as const,
        content:
          "Based on the assessment summary, I can help you create personalized recommendations. What specific aspect would you like me to focus on?",
      },
    ];
    setChatMessages(newMessages);
    setChatInput("");
  };

  const handleSegmentToggle = (segmentId: string) => {
    const newSelected = new Set(selectedSegments);
    if (newSelected.has(segmentId)) {
      newSelected.delete(segmentId);
    } else {
      newSelected.add(segmentId);
    }
    setSelectedSegments(newSelected);
  };

  const handleViewToggle = (viewType: string) => {
    const newSelected = new Set(selectedViews);
    if (newSelected.has(viewType)) {
      newSelected.delete(viewType);
    } else {
      newSelected.add(viewType);
    }
    setSelectedViews(newSelected);
  };

  const handleSegmentViewToggle = (segmentId: string) => {
    const newToggled = new Set(toggledViews);
    if (newToggled.has(segmentId)) {
      newToggled.delete(segmentId);
    } else {
      newToggled.add(segmentId);
    }
    setToggledViews(newToggled);
  };

  const filteredSegments = segmentData.filter((segment) =>
    selectedSegments.has(segment.id)
  );
  return {
    errors,
    isLoading,
    isEditMode,
    segmentData,
    toggledViews,
    playingSegment,
    filteredSegments,
    selectedSegments,
    // Functions
    togglePlay,
    handleChatSubmit,
    splitTextIntoPoints,
    handleSegmentToggle,
    handleSegmentViewToggle,
  };
};

export default useSummaryView;
